import React, { useState } from 'react'

import { useDispatch } from 'react-redux'
import { makeStyles } from '@mui/styles'
import IconLogo from '@assets/images/logo/svg/logo.svg'
import IconLogoMbo from '@assets/images/logo/svg/logoV2.svg'
import IllustrationRating from '@assets/images/csat/rating-illu.svg?url'
import IconClose from '@assets/images/icons/v2/close.svg'
import lang from '@/lang'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import { ButtonSquare } from '@/modules/base/components/buttons'
import { TextareaInput } from '@/modules/base/components/inputs'
import Rating from '@/modules/base/components/csat-modal/rating/rating'
import ThemeInterface from '@/theme-interface'
import PerfectScrollbar from 'react-perfect-scrollbar'
import clsx from 'clsx'
import CsatService from '@/modules/base/components/csat-modal/csatService'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { fetchData } from '@/redux/ducks/user/info'
import { csatFeaturesMapping } from '@/modules/base/components/csat-modal/constants'
import { isMeinBuero } from '@/utils/build-env'

const isMeinbuero = isMeinBuero()

const useStyles = makeStyles((theme: ThemeInterface) => {
  return {
    container: {
      display: 'flex',
      justifyContent: 'space-between',
      height: '100%',
    },
    leftContainer: {
      '& > :first-child': {
        paddingTop: 20,
      },
      paddingLeft: 30,
      paddingRight: 30,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    rightContainer: {
      borderLeft: theme.border,
      width: 'auto',
      minWidth: 340,
      backgroundColor: theme.sidebarColorModal,
    },
    icon: {
      position: 'absolute',
      top: 140,
      right: 35,
    },
    confirmButton: {
      width: 210,
    },
    abort: {
      cursor: 'pointer',
      fontSize: theme.fontSizes.default,
      color: theme.black,
      width: 'auto',
      height: 'auto',
      justifyContent: 'center',
      alignItems: 'center',
      display: 'flex',
    },
    buttonContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      height: '100%',
      alignItems: 'center',
      gap: 20,
      paddingBottom: 40,
    },
    title: {
      fontSize: 26,
      textAlign: 'center',
      padding: '20px 0',
    },
    subtext: {
      fontSize: theme.fontSizes.default,
      textAlign: 'center',
    },
    textArea: {
      width: 390,
      height: 90,
    },
    content: {
      width: 390,
      gap: 20,
      display: 'flex',
      flexDirection: 'column',
    },
    perfectScrollbar: {
      padding: '10px 20px 0',
      display: 'flex',
      flexDirection: 'column',
    },
    scrollbarContainer: {
      height: 320,
    },
    closeButton: {
      border: 0,
      backgroundColor: 'transparent',
      padding: 10,
      margin: '10px 20px',
      outline: 'none',
      position: 'absolute',
      right: 0,
    },
  }
})

interface CsatContentProps {
  feature: string;
  openAfterCount?: number;
  showCloseIcon: boolean;
  onAbort: () => void;
}

const CsatContent: React.FC<CsatContentProps> = (props) => {
  const {
    feature,
    openAfterCount,
    showCloseIcon,
    onAbort,
  } = props
  const classes = useStyles()

  const dispatch = useDispatch()
  const [notes, setNotes] = useState<string>('')
  const [rating, setRating] = useState<number | null>(null)

  const onConfirmClick = async() => {
    try {
      await CsatService.createFeature({ feature: csatFeaturesMapping[feature], notes, rating })
      dispatch(fetchData())
      onAbort?.()
      toast.showToast({ message: lang.csat.success })
    } catch (error) {
      toast.showToast({ message: lang.defaultErrorMessage, type: 'error' })
    } finally {
      CsatService.updateStoredFeatures({ feature, openAfterCount, openAfterCountFulfilled: true })
    }
  }

  const onAbortClick = async(showAgain = true) => {
    try {
      await CsatService.abortFeature({ feature: csatFeaturesMapping[feature], showAgain })
      dispatch(fetchData())
    } catch (error) {
      console.error(error) // just for analyze, we dont want to show an toast if abort doesn't work
    } finally {
      CsatService.updateStoredFeatures({ feature, openAfterCount, openAfterCountFulfilled: true })
      onAbort?.()
    }
  }

  return (
    <div className={classes.container}>
      <div className={classes.leftContainer}>
        <SVGIcon svg={isMeinbuero ? IconLogoMbo : IconLogo} width="200px" height="60px" />
        <span className={classes.title}>{lang.csat.features[feature].title}</span>
        <div className={classes.scrollbarContainer}>
          <PerfectScrollbar options={{ suppressScrollX: true }} className={clsx(classes.perfectScrollbar, 'forcePerfectScrollbarRight')}>
            <div className={classes.content}>
              <Rating maxRating={5} onRatingSelected={(val) => setRating(val)} />
              {lang.csat.features[feature].subtext && <span className={classes.subtext}>{lang.csat.features[feature].subtext}</span>}
              <TextareaInput
                placeholder={lang.csat.inputs.textInput.placeholder}
                value={notes}
                onChange={(val) => setNotes(val)}
                className={classes.textArea}
                useFormik={false}
                maxLength={250}
              />
            </div>
          </PerfectScrollbar>
        </div>
        <div className={classes.buttonContainer}>
          <ButtonSquare
            label={lang.csat.buttons.confirm}
            dataQsId='confirm-csat'
            className={classes.confirmButton}
            onClick={onConfirmClick}
            disabled={!rating}
          />
          <div className={classes.abort} onClick={() => onAbortClick(false)}>{lang.csat.buttons.doNotEvaluate}</div>
        </div>
      </div>
      <div className={classes.rightContainer}>
        {showCloseIcon && (
          <button onClick={() => onAbortClick(true)} className={classes.closeButton}>
            <SVGIcon svg={IconClose} height='10px' width='10px' />
          </button>
        )}
        <SVGIcon className={classes.icon} svg={IllustrationRating} width='345px' height='345px' />
      </div>
    </div>
  )
}

export default CsatContent
